<template>
  <div class="chart-container">
    <canvas ref="jalizbanChart"></canvas>
  </div>
</template>

<script>
import Chart from "chart.js";

export default {
  name: "JalizbanChart",
  props: {
    chartData: {
      type: Array,
      default: () => [],
    },
    chartDataLegend: {
      type: Array,
    },
    chartTitle: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      myChart: null,
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.renderChart();
    });
    window.addEventListener("resize", this.resizeChart);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.resizeChart);
    this.destroyChart();
  },
  watch: {
    chartData: {
      deep: true,
      handler() {
        this.scheduleRender();
      },
    },
    chartTitle() {
      this.scheduleRender();
    },
  },
  methods: {
    scheduleRender() {
      this.$nextTick(() => {
        this.renderChart();
      });
    },
    getLabel(item, index) {
      if (!item) return String(index + 1);
      if (item.date) {
        if (typeof item.date === "string") return item.date;
        if (item.date.display_date) return item.date.display_date;
        if (item.date.value) return item.date.value;
      }
      if (item.display_date) return item.display_date;
      return String(index + 1);
    },
    getMinValue(item) {
      if (item == null) return null;
      if (typeof item.min === "number") return item.min;
      if (item.min && typeof item.min.value === "number") return item.min.value;
      if (item.temp && typeof item.temp.min === "number") return item.temp.min;
      return null;
    },
    getMaxValue(item) {
      if (item == null) return null;
      if (typeof item.max === "number") return item.max;
      if (item.max && typeof item.max.value === "number") return item.max.value;
      if (item.temp && typeof item.temp.max === "number") return item.temp.max;
      return null;
    },
    median(values) {
      if (!values.length) return 0;
      const sorted = values.slice().sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      return sorted.length % 2 === 0
          ? (sorted[mid - 1] + sorted[mid]) / 2
          : sorted[mid];
    },
    /**
     * Replace sudden single-point spikes with neighbor interpolation,
     * then apply a light moving average for a softer trend line.
     */
    normalizeSeries(values) {
      const series = values.map((value) =>
          typeof value === "number" && !Number.isNaN(value) ? value : null
      );

      const valid = series.filter((value) => value !== null);
      if (valid.length < 3) return series;

      const consecutiveDiffs = [];
      for (let i = 1; i < series.length; i += 1) {
        if (series[i] !== null && series[i - 1] !== null) {
          consecutiveDiffs.push(Math.abs(series[i] - series[i - 1]));
        }
      }

      const typicalJump = this.median(consecutiveDiffs);
      const spikeThreshold = Math.max(typicalJump * 2.2, 1e-6);

      const despiked = series.slice();
      for (let i = 1; i < despiked.length - 1; i += 1) {
        const current = despiked[i];
        const prev = despiked[i - 1];
        const next = despiked[i + 1];
        if (current === null || prev === null || next === null) continue;

        const jumpFromPrev = Math.abs(current - prev);
        const jumpToNext = Math.abs(next - current);
        const neighborGap = Math.abs(next - prev);

        const isSuddenSpike =
            jumpFromPrev > spikeThreshold &&
            jumpToNext > spikeThreshold &&
            neighborGap < jumpFromPrev * 0.65;

        if (isSuddenSpike) {
          despiked[i] = (prev + next) / 2;
        }
      }

      // light 3-point moving average to reduce remaining jagged edges
      const smoothed = despiked.slice();
      for (let i = 1; i < despiked.length - 1; i += 1) {
        const prev = despiked[i - 1];
        const current = despiked[i];
        const next = despiked[i + 1];
        if (prev === null || current === null || next === null) continue;
        smoothed[i] = prev * 0.25 + current * 0.5 + next * 0.25;
      }

      return smoothed.map((value) =>
          value === null ? null : Number(value.toFixed(4))
      );
    },
    buildDatasets() {
      const labels = [];
      const rawMax = [];
      const rawMin = [];

      this.chartData.forEach((item, index) => {
        labels.push(this.getLabel(item, index));
        rawMin.push(this.getMinValue(item));
        rawMax.push(this.getMaxValue(item));
      });

      return {
        labels,
        maxDatasets: this.normalizeSeries(rawMax),
        minDatasets: this.normalizeSeries(rawMin),
      };
    },
    getChartConfig(labels, maxDatasets, minDatasets, title) {
      return {
        type: "line",
        data: {
          labels,
          datasets: [
            {
              label: "بیشترین مقدار",
              data: maxDatasets,
              borderColor: "#1fa971",
              backgroundColor: "rgba(31, 169, 113, 0.12)",
              borderWidth: 2.5,
              pointBackgroundColor: "#1fa971",
              pointBorderColor: "#ffffff",
              pointBorderWidth: 2,
              pointRadius: 3,
              pointHoverRadius: 6,
              pointHitRadius: 10,
              lineTension: 0.45,
              fill: true,
              spanGaps: true,
            },
            {
              label: "کمترین مقدار",
              data: minDatasets,
              borderColor: "#e35a5a",
              backgroundColor: "rgba(227, 90, 90, 0.08)",
              borderWidth: 2.5,
              pointBackgroundColor: "#e35a5a",
              pointBorderColor: "#ffffff",
              pointBorderWidth: 2,
              pointRadius: 3,
              pointHoverRadius: 6,
              pointHitRadius: 10,
              lineTension: 0.45,
              fill: true,
              spanGaps: true,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: {
            padding: {
              top: 8,
              right: 12,
              bottom: 4,
              left: 8,
            },
          },
          legend: {
            position: "bottom",
            labels: {
              fontFamily: "'iranyekan'",
              fontSize: 12,
              fontColor: "#4c4d4e",
              usePointStyle: true,
              padding: 18,
            },
          },
          title: {
            display: true,
            fontFamily: "'iranyekan'",
            fontSize: 14,
            fontColor: "#2f3031",
            fontStyle: "normal",
            padding: 16,
            text: title,
            rtl: true,
          },
          elements: {
            line: {
              borderCapStyle: "round",
              borderJoinStyle: "round",
            },
          },
          scales: {
            yAxes: [
              {
                gridLines: {
                  color: "rgba(76, 77, 78, 0.08)",
                  zeroLineColor: "rgba(76, 77, 78, 0.12)",
                  drawBorder: false,
                },
                ticks: {
                  fontSize: 11,
                  fontFamily: "'iranyekan'",
                  fontColor: "#8a8b8c",
                  padding: 8,
                  maxTicksLimit: 6,
                },
              },
            ],
            xAxes: [
              {
                gridLines: {
                  display: false,
                  drawBorder: false,
                },
                ticks: {
                  fontSize: 11,
                  fontFamily: "'iranyekan'",
                  fontColor: "#8a8b8c",
                  padding: 6,
                  maxRotation: 0,
                  autoSkip: true,
                  maxTicksLimit: 8,
                },
              },
            ],
          },
          tooltips: {
            mode: "index",
            intersect: false,
            bodySpacing: 6,
            titleSpacing: 4,
            bodyFontSize: 12,
            titleFontSize: 12,
            bodyFontFamily: "'iranyekan'",
            titleFontFamily: "'iranyekan'",
            rtl: true,
            backgroundColor: "rgba(255,255,255, 0.96)",
            bodyFontColor: "#2f3031",
            titleFontColor: "#2f3031",
            borderColor: "rgba(76, 77, 78, 0.12)",
            borderWidth: 1,
            xPadding: 12,
            yPadding: 10,
            caretPadding: 8,
            displayColors: true,
            bodyAlign: "right",
            titleAlign: "right",
          },
        },
      };
    },
    destroyChart() {
      if (this.myChart !== null) {
        this.myChart.destroy();
        this.myChart = null;
      }
    },
    renderChart() {
      if (!Array.isArray(this.chartData) || this.chartData.length === 0) {
        this.destroyChart();
        return;
      }

      const { labels, maxDatasets, minDatasets } = this.buildDatasets();
      const title = "نمودار " + (this.chartTitle || "") + " روزانه";

      if (this.myChart) {
        this.myChart.data.labels = labels;
        this.myChart.data.datasets[0].data = maxDatasets;
        this.myChart.data.datasets[1].data = minDatasets;
        if (this.myChart.options && this.myChart.options.title) {
          this.myChart.options.title.text = title;
        }
        this.myChart.update();
        return;
      }

      const canvas = this.$refs.jalizbanChart;
      if (!canvas) return;

      this.myChart = new Chart(
          canvas.getContext("2d"),
          this.getChartConfig(labels, maxDatasets, minDatasets, title)
      );
    },
    resizeChart() {
      if (this.myChart) {
        this.myChart.resize();
      }
    },
  },
};
</script>

<style>
.chart-container {
  position: relative;
  width: 100%;
  height: 42vh;
  min-height: 280px;
  margin-top: 20px;
  padding: 16px 12px 8px;
  border-radius: 16px;
  background: linear-gradient(180deg, #fbfcfd 0%, #f5f7f8 100%);
  border: 1px solid rgba(168, 169, 171, 0.25);
  box-sizing: border-box;
}

@media (max-width: 767px) {
  .chart-container {
    height: 38vh;
    min-height: 240px;
    margin-top: 24px;
    padding: 12px 8px 6px;
  }
}

@media (max-width: 575px) {
  .chart-container {
    height: 34vh;
    min-height: 220px;
    margin-top: 20px;
  }
}
</style>
